import { useState, useEffect, type ReactNode } from 'react'
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  useHover,
  useFocus,
  useDismiss,
  useRole,
  useInteractions,
  FloatingPortal,
  Placement
} from '@floating-ui/react'

export interface TooltipProps {
  /**
   * The content to display in the tooltip
   */
  content: ReactNode
  /**
   * The element that triggers the tooltip
   */
  children: ReactNode
  /**
   * Placement of the tooltip relative to the trigger
   */
  placement?: Placement
  /**
   * Delay in milliseconds before showing the tooltip on hover
   */
  delay?: number
  /**
   * Whether the tooltip is disabled
   */
  disabled?: boolean
  /**
   * Additional className for the tooltip container
   */
  className?: string
}

export const Tooltip = ({
  content,
  children,
  placement = 'top',
  delay = 300,
  disabled = false,
  className = ''
}: TooltipProps) => {
  const [isOpen, setIsOpen] = useState(false)

  const { refs, floatingStyles, context, isPositioned } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement,
    strategy: 'absolute',
    whileElementsMounted: autoUpdate,
    middleware: [offset(8), flip(), shift({ padding: 8 })]
  })

  const hover = useHover(context, {
    delay: { open: delay, close: 0 },
    enabled: !disabled
  })
  const focus = useFocus(context, { enabled: !disabled })
  const dismiss = useDismiss(context, {
    enabled: true,
    outsidePress: true,
    outsidePressEvent: 'mousedown',
    referencePress: true,
    referencePressEvent: 'mousedown',
    escapeKey: true,
    ancestorScroll: true
  })
  const role = useRole(context, { role: 'tooltip' })

  const { getReferenceProps, getFloatingProps } = useInteractions([hover, focus, dismiss, role])

  // Dismiss tooltip on scroll events
  useEffect(() => {
    if (!isOpen || disabled) return

    const handleScroll = () => {
      setIsOpen(false)
    }

    // Listen to scroll events on window and all scrollable ancestors
    window.addEventListener('scroll', handleScroll, true)
    document.addEventListener('scroll', handleScroll, true)

    return () => {
      window.removeEventListener('scroll', handleScroll, true)
      document.removeEventListener('scroll', handleScroll, true)
    }
  }, [isOpen, disabled])

  if (disabled || !content) {
    return <>{children}</>
  }

  return (
    <>
      <span ref={refs.setReference} {...getReferenceProps()}>
        {children}
      </span>
      {isOpen && (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            style={{
              ...floatingStyles,
              opacity: isPositioned ? 1 : 0,
              pointerEvents: 'none',
              transition: isPositioned ? 'opacity 0.15s ease-out' : 'none'
            }}
            className={`mp-agent-tooltip ${className}`.trim()}
            {...getFloatingProps()}
          >
            {content}
          </div>
        </FloatingPortal>
      )}
    </>
  )
}

export default Tooltip
